import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Collapse from 'react-bootstrap/Collapse';
import Swal from 'sweetalert2';
import AssetServices from '../../../services/AssetServices';
import AccessoriesService from '../../../services/AccessoriesService';

const AssignAccessories = () => {
  const [open2, setOpen2] = useState(true);
  const [assets, setAssets] = useState([]);
  const [uniquePartNumbers, setUniquePartNumbers] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const user = JSON.parse(localStorage.getItem('userDetails'));
  const storeList = user?.data?.user?.store || [];

  const [formValues, setFormValues] = useState({
    name: '',
    description: '',
    partNumber: '',
    serialNumber: '',
    asset: '',
    status: 'available',
    active: true,
    store: storeList.length > 0 ? storeList[0]._id : '',
    quantity: 1,
    purchaseDate: '' // Added purchaseDate here
  });

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const res = await AssetServices.getAllAssets();
        const assetData = res?.data?.assets || [];
        setAssets(assetData);

        const partsSet = new Set();
        assetData.forEach((asset) => {
          if (asset.masterPart && asset.masterPart.partNo) {
            partsSet.add(String(asset.masterPart.partNo));
          }
        });
        setUniquePartNumbers([...partsSet]);
      } catch (error) {
        console.error('Error fetching assets:', error);
        setAssets([]);
      }
    };
    fetchAssets();
  }, []);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues({
      ...formValues,
      [name]: type === 'checkbox' ? checked : value,
    });

    if (name === 'partNumber') {
      const filtered = assets.filter(
        (asset) => String(asset.masterPart.partNo) === value
      );
      setFilteredAssets(filtered);

      setFormValues((prev) => ({
        ...prev,
        serialNumber: '',
        asset: '',
      }));
    }

    if (name === 'serialNumber') {
      const selectedAsset = filteredAssets.find(
        (asset) => asset.serialNumber === value
      );

      if (selectedAsset) {
        setFormValues((prev) => ({
          ...prev,
          asset: selectedAsset._id,
        }));
      }
    }
  };

  const validateFields = () => {
    if (!formValues.partNumber) {
      Swal.fire('Validation Error', 'Part Number is required.', 'error');
      return false;
    }
    if (!formValues.serialNumber) {
      Swal.fire('Validation Error', 'Serial Number is required.', 'error');
      return false;
    }
    if (!formValues.name.trim()) {
      Swal.fire('Validation Error', 'Accessory Name is required.', 'error');
      return false;
    }
    if (!formValues.status) {
      Swal.fire('Validation Error', 'Status is required.', 'error');
      return false;
    }
    if (!formValues.store) {
      Swal.fire('Validation Error', 'Store selection is required.', 'error');
      return false;
    }
    if (!formValues.description.trim()) {
      Swal.fire('Validation Error', 'Description is required.', 'error');
      return false;
    }
    if (!formValues.asset) {
      Swal.fire('Validation Error', 'Asset selection is missing (internal error).', 'error');
      return false;
    }
    if (formValues.quantity === undefined || formValues.quantity === null || formValues.quantity === '') {
      Swal.fire('Validation Error', 'Quantity is required.', 'error');
      return false;
    }
    if (Number(formValues.quantity) < 1) {
      Swal.fire('Validation Error', 'Quantity must be greater than or equal to 1.', 'error');
      return false;
    }
    if (!formValues.purchaseDate) {
      Swal.fire('Validation Error', 'Purchase Date is required.', 'error');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateFields()) {
      return;
    }

    try {
      await AccessoriesService.createAccessories(formValues);
      Swal.fire('Success', 'Accessory Assigned successfully', 'success');

      setFormValues({
        name: '',
        description: '',
        partNumber: '',
        serialNumber: '',
        asset: '',
        status: 'available',
        active: true,
        store: storeList.length > 0 ? storeList[0]._id : '',
        quantity: 1,
        purchaseDate: '' // Reset purchaseDate after submit
      });
      setFilteredAssets([]);
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Error adding accessory', 'error');
    }
  };

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="filter cm-content-box box-primary">
          <div className="content-title">
            <div className="cpa">
              <i className="fas fa-file-word me-2"></i> Assign Accessory
            </div>
            <div className="tools">
              <Link
                to="#"
                className={`SlideToolHeader ${open2 ? 'collapse' : 'expand'}`}
                onClick={() => setOpen2(!open2)}
              >
                <i className="fas fa-angle-up"></i>
              </Link>
            </div>
          </div>

          <Collapse in={open2}>
            <div className="cm-content-body form excerpt">
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="form-group mb-3 col-md-6">
                      <label>Part Number *</label>
                      <select
                        name="partNumber"
                        className="form-select"
                        value={formValues.partNumber}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Part Number</option>
                        {uniquePartNumbers.map((partNo) => (
                          <option key={partNo} value={partNo}>
                            {partNo}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group mb-3 col-md-6">
                      <label>Asset Serial Number *</label>
                      <select
                        name="serialNumber"
                        className="form-select"
                        value={formValues.serialNumber}
                        onChange={handleInputChange}
                        disabled={!formValues.partNumber}
                      >
                        <option value="">Select Serial Number</option>
                        {filteredAssets.map((asset) => (
                          <option key={asset._id} value={asset.serialNumber}>
                            {asset.serialNumber}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group mb-3 col-md-6">
                      <label>Accessory Name *</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control form-control-sm"
                        value={formValues.name}
                        onChange={handleInputChange}
                        placeholder="Enter accessory name"
                      />
                    </div>

                    <div className="form-group mb-3 col-md-6">
                      <label>Status *</label>
                      <select
                        name="status"
                        className="form-select"
                        value={formValues.status}
                        onChange={handleInputChange}
                      >
                        <option value="available">Available</option>
                        <option value="assigned">Assigned</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="retired">Retired</option>
                      </select>
                    </div>

                    <div className="form-group mb-3 col-md-6">
                      <label>Store *</label>
                      <select
                        name="store"
                        className="form-select"
                        value={formValues.store}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Store</option>
                        {storeList.map((store) => (
                          <option key={store._id} value={store._id}>
                            {store.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group mb-3 col-md-6">
                      <label>Quantity *</label>
                      <input
                        type="number"
                        name="quantity"
                        className="form-control form-control-sm"
                        value={formValues.quantity}
                        min="1"
                        onChange={handleInputChange}
                        placeholder="Enter quantity"
                      />
                    </div>

                    <div className="form-group mb-3 col-md-6">
                      <label>Purchase Date *</label>
                      <input
                        type="date"
                        name="purchaseDate"
                        className="form-control"
                        value={formValues.purchaseDate}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group mb-3 col-md-6">
                      <label>Description</label>
                      <textarea
                        name="description"
                        rows="1"
                        className="form-control form-control-sm"
                        value={formValues.description}
                        onChange={handleInputChange}
                        placeholder="Enter description (optional)"
                      />
                    </div>

                    <div className="form-group mb-3 col-md-12">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          name="active"
                          checked={formValues.active}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label">Active</label>
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary me-2">
                    Add
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => {
                      setFormValues({
                        name: '',
                        description: '',
                        partNumber: '',
                        serialNumber: '',
                        asset: '',
                        status: 'available',
                        active: true,
                        store: storeList.length > 0 ? storeList[0]._id : '',
                        quantity: 1,
                        purchaseDate: '' // Reset purchaseDate on cancel
                      });
                      setFilteredAssets([]);
                    }}
                  >
                    Cancel
                  </button>
                </form>
              </div>
            </div>
          </Collapse>
        </div>
      </div>
    </div>
  );
};

export default AssignAccessories;
